import React, { useState } from "react";
import {
  SidebarComponent,
  SidebarModel,
} from "@syncfusion/ej2-react-navigations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faChevronLeft,
  faChevronRight,
  faBars,
} from "@fortawesome/free-solid-svg-icons";
import { get, isNil } from "lodash";

export interface DockPanelItem {
  title: string;
  content: React.ReactNode;
  icon?: IconDefinition;
}

export interface DockPanel {
  isDocked: boolean;
  items: DockPanelItem[];
  settings?: SidebarModel;
}

export interface SplitPanelProps {
  leftPanel?: DockPanel;
  rightPanel?: DockPanel;
  headerContent?: React.ReactNode;
  centerPanel: React.ReactNode;
  footerContent?: React.ReactNode;
  className?: string;
}

const SplitPanel: React.FC<SplitPanelProps> = ({
  leftPanel,
  rightPanel,
  headerContent,
  centerPanel,
  footerContent,
  className = "",
}) => {
  const [leftSelectedItem, setLeftSelectedItem] = useState<string | null>(
    leftPanel?.items && leftPanel.items.length > 0 ? "left-0" : null
  );
  const [rightSelectedItem, setRightSelectedItem] = useState<string | null>(
    rightPanel?.items && rightPanel.items.length > 0 ? "right-0" : null
  );
  const [leftPanelOpen, setLeftPanelOpen] = useState(
    isNil(leftPanel?.isDocked) ? true : !leftPanel?.isDocked
  );
  const [rightPanelOpen, setRightPanelOpen] = useState(
    isNil(rightPanel?.isDocked) ? true : !rightPanel?.isDocked
  );

  // Helper function to extract index from selected item ID
  const getSelectedIndex = (selectedItem: string | null): number => {
    if (!selectedItem) return -1;
    return parseInt(selectedItem.split("-")[1]);
  };

  // Default Syncfusion Sidebar settings
  const defaultSidebarSettings: SidebarModel = {
    type: "Auto",
    ...leftPanel?.settings,
  };

  const defaultRightSidebarSettings: SidebarModel = {
    type: "Auto",
    ...rightPanel?.settings,
  };

  const handleLeftItemClick = (_item: DockPanelItem, index: number) => {
    const itemId = `left-${index}`;
    setLeftSelectedItem(itemId);
    if (!leftPanelOpen) {
      setLeftPanelOpen(true);
    }
  };

  const handleRightItemClick = (_item: DockPanelItem, index: number) => {
    const itemId = `right-${index}`;
    setRightSelectedItem(itemId);
    if (!rightPanelOpen) {
      setRightPanelOpen(true);
    }
  };

  const toggleLeftPanel = () => {
    setLeftPanelOpen(!leftPanelOpen);
  };

  const toggleRightPanel = () => {
    setRightPanelOpen(!rightPanelOpen);
  };

  const getContainerClasses = () => {
    const classes = ["split-panel-container"];
    if (leftPanel && leftPanelOpen) classes.push("left-panel-open");
    if (rightPanel && rightPanelOpen) classes.push("right-panel-open");
    if (className) classes.push(className);
    return classes.join(" ");
  };

  const renderSelectedContent = (
    selectedItem: string | null,
    panel: DockPanel | undefined
  ) => {
    if (!selectedItem || !panel) return null;
    const index = getSelectedIndex(selectedItem);
    const item = get(panel, ["items", index]);
    return item ? item.content : null;
  };

  return (
    <div className={getContainerClasses()}>
      {headerContent && (
        <div className="split-panel-header">{headerContent}</div>
      )}

      <div className="split-panel-body">
        {leftPanel && (
          <SidebarComponent
            className="split-panel-left-sidebar"
            width={leftPanelOpen ? "280px" : "48px"}
            showBackdrop={false}
            closeOnDocumentClick={false}
            {...defaultSidebarSettings}
          >
            <div className="hamburger-menu" onClick={toggleLeftPanel}>
              <FontAwesomeIcon
                icon={leftPanelOpen ? faChevronLeft : faBars}
                title={leftPanelOpen ? "Collapse panel" : "Expand panel"}
              />
            </div>
            <div className="sidebar-menu">
              {leftPanel.items.map((item, index) => {
                const itemId = `left-${index}`;
                return (
                  <div
                    key={index}
                    className={`panel-item ${leftSelectedItem === itemId ? "selected" : ""}`}
                    onClick={() => handleLeftItemClick(item, index)}
                    title={leftPanelOpen ? "" : item.title}
                  >
                    {item.icon && (
                      <FontAwesomeIcon icon={item.icon} className="item-icon" />
                    )}
                    <span className="item-text">{item.title}</span>
                  </div>
                );
              })}
            </div>
            {leftPanelOpen && leftSelectedItem && (
              <div className="dock-panel-content">
                {/* <div className="dock-panel-header">
                  <div className="dock-panel-title">
                    {get(leftPanel, [
                      "items",
                      getSelectedIndex(leftSelectedItem),
                      "title",
                    ])}
                  </div>
                </div> */}
                <div className="dock-panel-body">
                  {renderSelectedContent(leftSelectedItem, leftPanel)}
                </div>
              </div>
            )}
          </SidebarComponent>
        )}

        <div className="split-panel-center">
          <div className="split-panel-content">{centerPanel}</div>
        </div>

        {rightPanel && (
          <SidebarComponent
            className="split-panel-right-sidebar"
            width={rightPanelOpen ? "280px" : "48px"}
            showBackdrop={false}
            closeOnDocumentClick={false}
            {...defaultRightSidebarSettings}
          >
            <div className="hamburger-menu" onClick={toggleRightPanel}>
              <FontAwesomeIcon
                icon={rightPanelOpen ? faChevronRight : faBars}
                title={rightPanelOpen ? "Collapse panel" : "Expand panel"}
              />
            </div>
            <div className="sidebar-menu">
              {rightPanel.items.map((item, index) => {
                const itemId = `right-${index}`;
                return (
                  <div
                    key={index}
                    className={`panel-item ${rightSelectedItem === itemId ? "selected" : ""}`}
                    onClick={() => handleRightItemClick(item, index)}
                    title={rightPanelOpen ? "" : item.title}
                  >
                    {item.icon && (
                      <FontAwesomeIcon icon={item.icon} className="item-icon" />
                    )}
                    <span className="item-text">{item.title}</span>
                  </div>
                );
              })}
            </div>
            {rightPanelOpen && rightSelectedItem && (
              <div className="dock-panel-content">
                {/* <div className="dock-panel-header">
                  <div className="dock-panel-title">
                    {get(rightPanel, [
                      "items",
                      getSelectedIndex(rightSelectedItem),
                      "title",
                    ])}
                  </div>
                </div> */}
                <div className="dock-panel-body">
                  {renderSelectedContent(rightSelectedItem, rightPanel)}
                </div>
              </div>
            )}
          </SidebarComponent>
        )}
      </div>

      {footerContent && (
        <div className="split-panel-footer">{footerContent}</div>
      )}
    </div>
  );
};

export default SplitPanel;
